const logotext = "Rosota";
const meta = {
    title: "Rosota Inc. | AI-native Surgical Robotics",
    description: "로소타(Rosota Inc.)는 수술 데이터를 AI가 학습 가능한 인프라로 전환하고, 이를 AI-native 수술로봇으로 연결하는 대한민국 기반의 Surgical AI·Physical AI 로보틱스 스타트업입니다.",
};

// ── Feature flags — flip to toggle features on/off site-wide. ──
// coffeeChat: temporarily hidden. Set to `true` to restore the Coffee Chat
// tab and its /join/coffee route (see routes.js, pages/join/index.js).
const featureFlags = {
    coffeeChat: false,
};

// ── Vision page (home) — public-facing only. No strategy/product detail. ──
const visiondata = {
    vision: {
        en: "A world where every patient can access high-quality surgery.",
        ko: "모두가 최고의 수술을 받을 수 있는 세상.",
    },
    mission: {
        en: "We transform surgical data into learnable AI infrastructure and connect it to surgical robotic systems.",
        ko: "수술 데이터를 학습 가능한 AI 인프라로 전환하고, 이를 수술로봇 시스템으로 연결합니다.",
    },
    narrative: [
        {
            en: "Korean surgeons already demonstrate world-class surgical excellence. Yet their techniques, judgment, and intraoperative decision-making have not been sufficiently captured as data, and the infrastructure to translate them into an industry is only beginning to emerge.",
            ko: "대한민국 의료진의 수술 역량은 이미 세계 최고 수준입니다. 그러나 그 술기와 판단은 아직 충분히 데이터화되지 않았고, 이를 산업으로 연결하는 인프라는 이제 막 형성되고 있습니다.",
        },
        {
            en: "Most existing surgical robots remain centered on teleoperation — precisely transmitting the surgeon's hand movements. Rosota is preparing for the next generation.",
            ko: "기존 수술로봇은 의사의 손을 정밀하게 전달하는 teleoperation 중심이었다면, 로소타는 그 다음 세대를 준비합니다.",
        },
        {
            en: "We collect video, instrument motion, and sensor data generated during surgery, anonymize and de-identify them, and connect them to AI learning. Through this, we are building AI-native surgical robots that support surgeons' judgment and technical performance in real time.",
            ko: "수술 중 발생하는 영상, 기구 움직임, 센서 데이터를 수집하고 익명화·비식별화하여 AI 학습으로 연결합니다. 이를 통해 외과의의 판단과 술기를 실시간으로 지원하는 AI-native 수술로봇을 구현합니다.",
        },
    ],
    whyNow: {
        heading: { en: "Why now", ko: "Why now" },
        points: [
            {
                en: "Surgical robotics is still stuck in teleoperation — the surgeon drives every motion. The next generation is AI-native: robots that understand the procedure, not just relay the hands.",
                ko: "수술로봇은 지금도 teleoperation에 머물러 있습니다. 모든 움직임을 여전히 의사가 직접 조종하죠. 다음 세대는 수술을 '이해하는' AI-native 로봇입니다.",
            },
            {
                en: "The real bottleneck was never the hardware — it was data. High-quality surgical data is the foundation surgical AI has been missing.",
                ko: "진짜 병목은 하드웨어가 아니라 데이터였습니다. 양질의 수술 데이터야말로 수술 AI가 갖추지 못한 토대입니다.",
            },
            {
                en: "Now that surgery can be captured and learned from at scale, controlling execution — and the outcome — is finally within reach.",
                ko: "이제 수술을 대규모로 수집하고 학습할 수 있게 되면서, 실행을 그리고 결과를 제어하는 일이 비로소 가능해지고 있습니다.",
            },
        ],
    },
    roadmap: {
        heading: { en: "Where we're headed", ko: "Where we're headed" },
        stages: [
            {
                term: { en: "Short term", ko: "Short term" },
                text: {
                    en: "Build a proprietary surgical data pipeline — capturing video, instrument motion, and sensor data in real time, then de-identifying and structuring it for AI.",
                    ko: "독자적인 수술 데이터 파이프라인을 구축합니다. 영상·기구 움직임·센서 데이터를 실시간으로 수집하고, 비식별화해 AI 학습용으로 정제합니다.",
                },
                image: {
                    webp: "/image/systems/sensor-modules.webp",
                    jpg: "/image/systems/sensor-modules.jpg",
                    alt: {
                        en: "Rosota in-house data-acquisition modules",
                        ko: "로소타가 자체 제작한 데이터 수집 모듈",
                    },
                },
            },
            {
                term: { en: "Mid term", ko: "Mid term" },
                text: {
                    en: "Develop AI that understands the procedure and supports the surgeon's decisions in real time.",
                    ko: "수술 상황을 이해하고, 외과의의 의사결정을 실시간으로 지원하는 AI를 개발합니다.",
                },
                image: {
                    webp: "/image/systems/handheld-controller.webp",
                    jpg: "/image/systems/handheld-controller.jpg",
                    alt: {
                        en: "Rosota instrument capturing data during a procedure",
                        ko: "수술 중 데이터를 수집하는 로소타 기구",
                    },
                },
            },
            {
                term: { en: "Long term", ko: "Long term" },
                text: {
                    en: "Deliver an AI-native surgical robot that amplifies the surgeon — so more patients, anywhere, can receive world-class surgery.",
                    ko: "외과의의 역량을 증폭하는 AI-native 수술로봇을 만듭니다. 더 많은 환자가 어디에서나 세계 수준의 수술을 받을 수 있도록.",
                },
                image: {
                    webp: "/image/systems/or-console.webp",
                    jpg: "/image/systems/or-console.jpg",
                    alt: {
                        en: "Rosota data-capture rig running alongside a live procedure",
                        ko: "수술과 함께 가동 중인 로소타 데이터 수집 장비",
                    },
                },
            },
        ],
    },
    whyUs: {
        heading: { en: "Why Rosota", ko: "Why Rosota" },
        cards: [
            {
                label: { en: "Clinical origin", ko: "Clinical origin" },
                text: {
                    en: "Rosota was spun out of the MediSC Lab at the Department of Convergence Medicine, Seoul National University Hospital. We do not imagine problems from the outside — we define them inside the operating room.",
                    ko: "서울대학교병원 융합의학과 MediSC 연구실에서 출발했습니다. 로소타는 문제를 밖에서 상상하지 않고, 실제 수술실 안에서 정의합니다.",
                },
            },
            {
                label: { en: "Surgical data pipeline", ko: "Surgical data pipeline" },
                text: {
                    en: "The biggest bottleneck in AI surgical robotics is data. Rosota collects video, instrument motion, and sensor data generated during surgery in real time, anonymizes and de-identifies them, and converts them into AI-training data.",
                    ko: "AI 수술로봇의 가장 큰 병목은 데이터입니다. 로소타는 수술 중 발생하는 영상, 기구 움직임, 센서 데이터를 실시간으로 수집하고 익명화·비식별화하여 AI 학습 데이터로 전환합니다.",
                },
            },
            {
                label: { en: "AI-native robotics", ko: "AI-native robotics" },
                text: {
                    en: "While conventional surgical robots precisely transmit the surgeon's hand movements, Rosota aims to build next-generation AI-native surgical robots that understand the surgical situation and support surgeons' decision-making.",
                    ko: "기존 수술로봇이 의사의 손을 정밀하게 전달했다면, 로소타는 수술 상황을 이해하고 외과의의 의사결정을 지원하는 다음 세대 AI-native 수술로봇을 지향합니다.",
                },
            },
            {
                label: { en: "Sovereign surgical AI", ko: "Sovereign surgical AI" },
                text: {
                    en: "Rosota builds an integrated technology stack in-house, from data-acquisition devices to AI models and real-world robotic implementation. Based on this, Rosota is becoming an independent AI-native player in the global surgical robotics market.",
                    ko: "데이터 수집 장치부터 AI 모델, 실제 로봇 구현까지 이어지는 통합 기술 체계를 자체적으로 구축합니다. 로소타는 이를 바탕으로 글로벌 수술로봇 시장에서 독립적인 AI-native 플레이어가 될 것입니다.",
                },
            },
        ],
    },
    structure: {
        text: {
            en: "Rosota is built as two halves — Rosota Inc. and the research crew behind it. Business and research push each other forward.",
            ko: "Rosota는 사업체 Rosota Inc.와 이를 받치는 연구 크루, 두 축으로 움직입니다. 사업과 연구가 서로를 끌어올립니다.",
        },
        linkLabel: { en: "Meet the crew", ko: "Meet the crew" },
    },
    closing: {
        en: "We're building the surgical-robotics company that represents Korea on the world stage.",
        ko: "대한민국을 대표하는 수술로봇 기업을 만들어 갑니다.",
    },
};

const pressdata = {
    heading: { en: "In the Press", ko: "보도자료" },
    subheading: {
        en: "Selected coverage and announcements.",
        ko: "주요 언론 보도와 발표 소식입니다.",
    },
    empty: {
        en: "Press coverage is on the way. Check back soon.",
        ko: "보도자료가 곧 업데이트됩니다. 조금만 기다려 주세요.",
    },
    items: [
        {
            outlet: { en: "Money Today", ko: "머니투데이" },
            title: {
                en: "Rosota takes Korea's physical surgical-data AI to Silicon Valley",
                ko: "로소타, 美 실리콘밸리서 K의료 핵심 '물리적 수술 데이터 AI' 알린다",
            },
            date: "2026.08.04",
            url: "https://www.mt.co.kr/future/2026/08/04/2026080415350823261",
            image: "/image/press/moneytoday.jpg",
        },
        {
            outlet: { en: "StartupToday", ko: "스타트업투데이" },
            title: {
                en: "Rosota raises ₩1.5B seed to open the AI-native surgical robotics market",
                ko: "로소타, 15억 원 시드 투자 유치로 AI 네이티브 수술로봇 시장 개척",
            },
            date: "2026.07.02",
            url: "https://www.startuptoday.kr/news/articleView.html?idxno=52659",
            image: "/image/press/startuptoday.jpg",
        },
        {
            outlet: { en: "Korea M&A Economic News", ko: "한국M&A경제신문" },
            title: {
                en: "Rosota secures ₩1.5B seed round, accelerating AI surgical-robot development",
                ko: "로소타, 15억 원 규모 시드 투자 유치… AI 수술로봇 개발 가속화",
            },
            date: "2026.07.02",
            url: "https://www.kmnanews.com/news/articleView.html?idxno=10561",
            image: "/image/press/kmnanews.jpg",
        },
    ],
};

// ── About us — CEO introduction only. ──
const ceodata = {
    name: { en: "Yechan Seo", ko: "서예찬" },
    role: { en: "CEO & Founder", ko: "대표이사 · 창업자" },
    photo: "/image/ceo_yechan.jpg",
    greeting: {
        en: "Hello — I'm Yechan Seo, founder of Rosota. We are building the surgical-robotics company that will represent Korea on the world stage, and I'm looking for the people who will build it with us.",
        ko: "안녕하세요, Rosota 대표 서예찬입니다. 대한민국을 대표하는 수술로봇 기업을 함께 세워나갈 분들을 찾고 있습니다.",
    },
    bio: [
        { en: "B.S. candidate, Seoul National University College of Medicine", ko: "서울대학교 의과대학 의학과 재학" },
        { en: "Gyeonggi Science High School — top graduate in math & science", ko: "경기과학고등학교 수·과학 수석 졸업" },
        { en: "Korea National Team, International Mathematical Modeling Challenge (2019)", ko: "International Mathematical Modeling Challenge 한국 국가대표 (2019)" },
        { en: "Co-first author, international SCI journal", ko: "국제 SCI 저널 논문 공동 제1저자" },
        { en: "Future Talent Award, SNU College of Medicine (2024)", ko: "서울대학교 의과대학 미래인재상 수상 (2024)" },
        { en: "MIT Reality Hack — Hardware Track, Meta Top 3 (hosted by MIT Media Lab)", ko: "MIT Reality Hack 하드웨어 트랙 Meta Top 3 수상 (MIT Media Lab 주관)" },
    ],
    email: "yechan.seo@rosota.run",
};

// ── Join us — open positions (already public via the founder's letter). ──
const positions = [
    {
        title: "Robotics AI Researcher",
        tags: ["Imitation Learning", "Reinforcement Learning", "Sim"],
        desc: {
            en: "Build the learning stack for surgical action — imitation learning, reinforcement learning, and simulation.",
            ko: "수술 동작을 학습하는 AI 스택을 만듭니다 — Imitation Learning, 강화학습, 시뮬레이션.",
        },
    },
    {
        title: "3D Reconstruction Researcher",
        tags: ["Surgery", "Monocular Video"],
        desc: {
            en: "Reconstruct the surgical scene in 3D from monocular surgery video.",
            ko: "단안(monocular) 수술 영상에서 수술 장면을 3차원으로 복원합니다.",
        },
    },
];

const introdata = {
    title: "Rosota",
    animated: {
        first: "The SOTA Model in Physical AI",
        second: "Automate the Analog World",
        third: "We are Robotics Nerds",
    },
    description: "인간중심환경에 최적화된, Physical AI 기반 맞춤형 로봇을 개발합니다.",
    your_img_url: "/rosota-banner.jpg",
};

const dataabout = {
    title: "About Us",
    aboutme: `We aspire to become Korea's leading AI-powered medical robotics company. By integrating cutting-edge artificial intelligence with innovative surgical robotics technology, we are building the foundation to become a world-class global medical device company. Our mission is to revolutionize healthcare through intelligent automation and precision robotics that enhance surgical outcomes and improve patient care worldwide.`,
};
const worktimeline = [{
        jobtitle: "Designer of week",
        where: "YAdfi",
        date: "2020",
    },
    {
        jobtitle: "Designer of week",
        where: "Jamalya",
        date: "2019",
    },
    {
        jobtitle: "Designer of week",
        where: "ALquds",
        date: "2019",
    },
];

const skills = [{
        name: "Python",
        value: 90,
    },
    {
        name: "Djano",
        value: 85,
    },
    {
        name: "Javascript",
        value: 80,
    },
    {
        name: "React",
        value: 60,
    },
    {
        name: "Jquery",
        value: 85,
    },
];

const services = [{
        title: "UI & UX Design",
        description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed at nisl euismod urna bibendum sollicitudin.",
    },
    {
        title: "Mobile Apps",
        description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed at nisl euismod urna bibendum sollicitudin.",
    },
    {
        title: "Wordpress Design",
        description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed at nisl euismod urna bibendum sollicitudin.",
    },
];

const dataportfolio = [
    {
        title: "SNUH Medical Robot",
        img: '/image/Logo_2.jpg',
        description: "Advanced teleoperated humanoid surgical robot system for automated laparoscopic procedures with precision control, developed in collaboration with Seoul National University Hospital.",
        link: "/github",
        price: "Contact for pricing"
    },
    {
        title: "LeRobot Platform",
        img: '/image/Logo_2.jpg',
        description: "High-throughput, modular robotics platform leveraging SO101 — engineered for performance, scalability, and open-source innovation.",
        link: "https://github.com/Rosota-SNU",
        price: "Contact for pricing"
    },
];

const contactConfig = {
    YOUR_EMAIL: "yechan.seo@rosota.run",
    description: "Keep Moving Forward with Us!"
};

const socialprofils = {
    instagram: "https://www.instagram.com/rosota.run/",
};
export {
    meta,
    dataabout,
    dataportfolio,
    worktimeline,
    skills,
    services,
    introdata,
    contactConfig,
    socialprofils,
    logotext,
    visiondata,
    ceodata,
    positions,
    pressdata,
    featureFlags,
};